import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Users, LayoutDashboard, ChevronDown, Check } from 'lucide-react';
import { cn } from '../lib/utils';

const RoleSwitcher = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  
  // Check if user has dual roles (Team Member who is also Staff)
  const hasStaffRole = user?.isAlsoStaff || user?.hasStaffRole || user?.linkedStaffId;
  const isManager = user?.role === 'manager' || user?.industryType === 'dj_company' || user?.industryType === 'event_planner' || user?.isTeamMember;
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          triggerRef.current && !triggerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);
  
  // Only show if user has both roles
  if (!hasStaffRole || !isManager) {
    return null;
  }
  
  // Determine current view based on path
  const isStaffView = location.pathname.startsWith('/staff-portal') || location.pathname.startsWith('/staff/my-profile') || location.pathname.startsWith('/my-assignments');
  const currentRole = isStaffView ? 'staff' : 'manager';
  
  const handleRoleSwitch = (role) => {
    setIsOpen(false);
    if (role === 'staff') {
      navigate('/staff-portal');
    } else {
      navigate('/events');
    }
  };
  
  return (
    <div className="relative">
      <button 
        ref={triggerRef}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#252540] border border-[#3D3D5C] hover:border-[#D4AF37]/50 transition-colors text-sm"
        data-testid="role-switcher-trigger"
      >
        {currentRole === 'staff' ? (
          <>
            <Users className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-white">Staff View</span>
          </>
        ) : (
          <>
            <LayoutDashboard className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-white">Manager View</span>
          </>
        )}
        <ChevronDown className={cn("w-3 h-3 text-gray-400 transition-transform duration-200", isOpen && "rotate-180")} />
      </button>
      
      {isOpen && (
        <div 
          ref={dropdownRef}
          className="absolute right-0 top-full mt-2 w-56 rounded-lg border border-[#2D2D4A] bg-[#0B0F1A] shadow-xl z-[9999] overflow-hidden animate-in fade-in-0 zoom-in-95 duration-200"
        >
          <div className="p-1">
            <button
              onClick={() => handleRoleSwitch('manager')}
              className={cn(
                "w-full flex items-center justify-between px-3 py-2.5 rounded-md text-left transition-colors",
                currentRole === 'manager' ? "bg-[#D4AF37]/10" : "hover:bg-white/5"
              )}
              data-testid="role-switch-manager"
            >
              <div className="flex items-center gap-3">
                <LayoutDashboard className="w-4 h-4 text-gray-400" />
                <div>
                  <span className="block text-white text-sm">Manager Dashboard</span>
                  <span className="text-xs text-gray-500">Create events, schedule staff</span>
                </div>
              </div>
              {currentRole === 'manager' && <Check className="w-4 h-4 text-[#D4AF37]" />}
            </button>
            
            <button
              onClick={() => handleRoleSwitch('staff')}
              className={cn(
                "w-full flex items-center justify-between px-3 py-2.5 rounded-md text-left transition-colors",
                currentRole === 'staff' ? "bg-[#D4AF37]/10" : "hover:bg-white/5"
              )}
              data-testid="role-switch-staff"
            >
              <div className="flex items-center gap-3">
                <Users className="w-4 h-4 text-gray-400" />
                <div>
                  <span className="block text-white text-sm">Staff Portal</span>
                  <span className="text-xs text-gray-500">View assignments, check in</span>
                </div>
              </div>
              {currentRole === 'staff' && <Check className="w-4 h-4 text-[#D4AF37]" />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleSwitcher;
